import React from 'react';
import { createPortal } from 'react-dom';
import { getInitials } from '../../utils/nameUtils';
import { getPlayerNameColor, buildPlayerEloTooltipText, formatSessionGamesCountLabel } from '../../utils/playerUtils';
import { getGenderAvatarBg } from '../../utils/genderColors';
import EloChange from '../common/EloChange';
import { getPlayerAvatarPublicUrl } from '../../services/avatarService';
import { useStatsTooltip, StatsTooltipCaret } from './SessionPlayerPill';

/**
 * Player row for courts: avatar, truncated name (hover/focus for ratings tooltip), session games count, optional post-match delta.
 */
const PlayerActionDropdown = ({
  player,
  teamNumber,
  courtNumber,
  onRemove,
  availablePlayers = [],
  showEloChange = false,
  eloDelta = 0,
  isSocialSession = false,
  className = '',
  teammate = null,
  playerStreaks = {},
  useSinglesElo = false,
  dragActivatorProps = null,
  compact = false,
  /** Normalized session wait-metrics slice for this player (match preference, etc.) */
  waitMetricSlice = null,
  showEloTooltip = true,
}) => {
  const canHover = typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const displayEloChange = typeof eloDelta === 'object' && eloDelta !== null ? eloDelta.total : eloDelta;

  const statsTooltip = buildPlayerEloTooltipText(player, {
    useSinglesElo,
    eloChangeTotal: showEloChange ? displayEloChange : null,
    sessionWaitMetrics: waitMetricSlice,
  });

  const getPerformanceBadge = () => {
    const streak = playerStreaks[player.id];
    if (!streak) return null;
    const consecutive = streak.currentWinStreak ?? 0;
    if (consecutive >= 5) return { icon: '💎', tooltip: 'Elite Form: 5+ consecutive wins' };
    if (consecutive >= 3) return { icon: '🔥', tooltip: 'Hot Streak: 3+ consecutive wins' };
    return null;
  };

  const performanceBadge = getPerformanceBadge();

  const gamesLabel = formatSessionGamesCountLabel(player);

  const displayName = player?.name || '';

  const tooltipNameLine = statsTooltip.includes('\n')
    ? statsTooltip.slice(0, statsTooltip.indexOf('\n'))
    : statsTooltip;
  const tooltipBody =
    statsTooltip.includes('\n') ? statsTooltip.slice(statsTooltip.indexOf('\n') + 1) : '';
  const {
    showTooltip,
    setShowTooltip,
    tooltipRef,
    floatingRef,
    tooltipStyle,
    placement,
    onAnchorMouseEnter,
    onAnchorKeyboardOpen,
  } = useStatsTooltip({
    width: 280,
    estHeight: 96,
    gap: 6,
  });

  return (
    <div
      className={`group relative flex w-full cursor-default select-none items-center overflow-hidden rounded transition-colors hover:bg-black/5 dark:hover:bg-white/5 ${
        compact ? 'gap-1.5 px-2 py-1' : 'gap-2 px-2.5 py-1.5'
      } ${className}`.trim()}
    >
      <div
        ref={tooltipRef}
        className={`flex min-w-0 flex-1 items-center gap-2${
          dragActivatorProps ? ' cursor-grab touch-manipulation active:cursor-grabbing' : ''
        }`}
        onMouseEnter={
          showEloTooltip
            ? (e) => {
                onAnchorMouseEnter(e);
                if (canHover) setShowTooltip(true);
              }
            : undefined
        }
        onMouseLeave={showEloTooltip ? () => canHover && setShowTooltip(false) : undefined}
        {...(dragActivatorProps || {})}
      >
        <div
          className={`flex-shrink-0 overflow-hidden rounded-full font-semibold text-white ${
            compact ? 'h-6 w-6 text-xs' : 'h-8 w-8 text-sm'
          } ${getGenderAvatarBg(player.gender)} flex items-center justify-center`}
          title={player.name}
        >
          {player.avatar_url ? (
            <img
              src={getPlayerAvatarPublicUrl(player.avatar_url)}
              alt={player.name}
              className="h-full w-full object-cover"
            />
          ) : (
            getInitials(player.name)
          )}
        </div>

        <div
          title={player.name || undefined}
          className={`min-w-0 flex-1 truncate font-semibold ${showEloTooltip ? 'cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded' : ''} ${compact ? 'text-base sm:text-lg' : 'text-lg lg:text-xl'} ${getPlayerNameColor(player.gender)}`}
          onClick={
            showEloTooltip
              ? (e) => {
                  e.stopPropagation();
                  if (!canHover) setShowTooltip((prev) => !prev);
                }
              : undefined
          }
          onFocus={
            showEloTooltip
              ? () => {
                  onAnchorKeyboardOpen();
                  setShowTooltip(true);
                }
              : undefined
          }
          onBlur={showEloTooltip ? () => setShowTooltip(false) : undefined}
          tabIndex={showEloTooltip ? 0 : undefined}
          role={showEloTooltip ? 'button' : undefined}
          aria-label={showEloTooltip ? `View ratings for ${player.name}` : undefined}
        >
          {displayName}
        </div>

        {performanceBadge && (
          <span
            className={`flex-shrink-0 leading-none ${compact ? 'text-base' : 'text-lg'}`}
            title={performanceBadge.tooltip}
            role="img"
            aria-label={performanceBadge.tooltip}
          >
            {performanceBadge.icon}
          </span>
        )}

        <span
          className={`flex-shrink-0 whitespace-nowrap tabular-nums text-text-muted ${
            compact ? 'text-xs' : 'text-sm'
          }`}
          title="Games played this session"
        >
          {gamesLabel}
        </span>

        {showEloChange && displayEloChange !== undefined && displayEloChange !== 0 && (
          <span className="flex flex-shrink-0 items-center">
            <EloChange change={displayEloChange} showSign size="sm" />
          </span>
        )}
      </div>

      {showEloTooltip && showTooltip &&
        createPortal(
          <div
            ref={floatingRef}
            className="relative max-w-[min(260px,calc(100vw-16px))] whitespace-pre-line rounded-lg border border-border-medium bg-surface-subtle px-3 py-2 text-sm text-text-primary shadow-lg pointer-events-none dark:bg-surface-elevated dark:shadow-xl"
            style={tooltipStyle}
          >
            <div className="text-sm text-text-primary">
              {tooltipNameLine ? <div className="font-bold">{tooltipNameLine}</div> : null}
              {tooltipBody ? (
                <div className="mt-1 whitespace-pre-line font-normal">{tooltipBody}</div>
              ) : null}
            </div>
            <StatsTooltipCaret placement={placement} />
          </div>,
          document.body
        )}

      {onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove(player.id, teamNumber, courtNumber);
          }}
          className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-base leading-none text-text-muted opacity-0 transition-all hover:text-danger group-hover:opacity-100"
          title="Remove from court"
        >
          ×
        </button>
      )}
    </div>
  );
};

export default PlayerActionDropdown;
